/**
 * A única porta de saída da tela para a API.
 *
 * Tudo passa por aqui por dois motivos:
 *
 * 1. O token entra em UM lugar. Espalhar `fetch` pelas telas é como uma
 *    chamada acaba saindo sem `Authorization` e falha só em produção.
 * 2. O erro do servidor chega às telas em formato ÚNICO. A API devolve
 *    `{ mensagem, campos: [{campo, erro}] }` (ver `validar.ts`), e é isso
 *    que permite marcar o campo errado em vez de mostrar um alerta genérico
 *    que obriga o técnico a procurar o que ele digitou errado.
 *
 * O caminho é sempre relativo. Nunca há uma URL de API configurável: é o que
 * torna impossível a tela de homologação apontar para o banco de produção.
 */

const CHAVE_TOKEN = 'testesolo.token';

/**
 * O token de quem VOCÊ é, guardado enquanto você veste uma persona.
 *
 * «Entrar como» troca o token da sessão pelo da persona. O caminho de
 * volta é este aqui: o token próprio, intocado, esperando. Um «voltar»
 * que pedisse token novo ao servidor seria um jeito de renovar sessão
 * sem senha, e é por isso que a volta é local (0079).
 */
const CHAVE_TOKEN_ATOR = 'testesolo.token.ator';

export interface CampoInvalido { campo: string; erro: string }

export class ErroApi extends Error {
  readonly status: number;
  readonly campos: CampoInvalido[];
  /**
   * As recusas que o servidor manda junto da mensagem (`{ mensagem, recusas }`):
   * a importação do CPTu diz, item por item, o que não entrou (onda 312). As
   * outras telas não leem — o campo só existe quando o corpo o traz.
   */
  readonly recusas?: unknown[];
  /**
   * O corpo inteiro da recusa, quando ele traz mais que a frase — o 409 do
   * contato duplicado manda o `contatoId` do existente, e a janela oferece
   * «Abrir o contato existente» (onda 554, Tarefa 7).
   */
  readonly corpo?: Readonly<Record<string, unknown>>;

  constructor(status: number, mensagem: string, campos: CampoInvalido[] = [], recusas?: unknown[],
    corpo?: Readonly<Record<string, unknown>>) {
    super(mensagem);
    this.name = 'ErroApi';
    this.status = status;
    this.campos = campos;
    if (recusas) this.recusas = recusas;
    if (corpo) this.corpo = corpo;
  }

  /** O erro daquele campo, se houver. Alimenta o `<Campo erro={...}>`. */
  campo(nome: string): string | undefined {
    return this.campos.find((c) => c.campo === nome || c.campo.endsWith(`.${nome}`))?.erro;
  }
}

export const token = {
  ler: (): string | null => {
    try { return localStorage.getItem(CHAVE_TOKEN); } catch { return null; }
  },
  gravar: (t: string) => { try { localStorage.setItem(CHAVE_TOKEN, t); } catch { /* modo privado */ } },
  limpar: () => {
    try {
      localStorage.removeItem(CHAVE_TOKEN);
      localStorage.removeItem(CHAVE_TOKEN_ATOR);
    } catch { /* idem */ }
  },
};

/**
 * O token guardado para o caminho de volta do «Entrar como».
 *
 * Sair da sessão apaga os dois: deixar o token do ator para trás faria
 * o próximo a usar aquele navegador encontrar uma sessão pela metade.
 */
export const tokenDoAtor = {
  ler: (): string | null => {
    try { return localStorage.getItem(CHAVE_TOKEN_ATOR); } catch { return null; }
  },
  gravar: (t: string) => {
    try { localStorage.setItem(CHAVE_TOKEN_ATOR, t); } catch { /* modo privado */ }
  },
  limpar: () => {
    try { localStorage.removeItem(CHAVE_TOKEN_ATOR); } catch { /* idem */ }
  },
};

/**
 * Avisa a aplicação de que a sessão caiu.
 *
 * O `api()` não navega sozinho: quem decide para onde ir é a árvore de
 * componentes. Um `location.href` aqui dentro perderia o que o técnico
 * digitou sem aviso nenhum.
 */
type Ouvinte = () => void;
const ouvintesSessao = new Set<Ouvinte>();
export function aoPerderSessao(f: Ouvinte): () => void {
  ouvintesSessao.add(f);
  return () => ouvintesSessao.delete(f);
}

/* ── Conexão: quem está no ar, e quem quer saber ───────────────────────
 *
 * «Aviso claro de ausência de conexão» é requisito do §18, e o galpão com
 * sinal ruim é o ambiente NORMAL desta tela. O estado mora aqui, junto do
 * `fetch`, porque `navigator.onLine` mente: ele diz que há rede quando há
 * wi-fi sem internet, que é exatamente a situação do canteiro. Quem sabe
 * de verdade é a última chamada que foi ou não foi.
 */
export type EstadoDaConexao = 'no_ar' | 'sem_rede' | 'tentando';
let conexao: EstadoDaConexao = 'no_ar';
const ouvintesConexao = new Set<(e: EstadoDaConexao) => void>();

export const estadoDaConexao = () => conexao;

export function aoMudarConexao(f: (e: EstadoDaConexao) => void): () => void {
  ouvintesConexao.add(f);
  return () => ouvintesConexao.delete(f);
}

function anunciarConexao(e: EstadoDaConexao) {
  if (conexao === e) return;
  conexao = e;
  ouvintesConexao.forEach((f) => f(e));
}

try {
  addEventListener('offline', () => anunciarConexao('sem_rede'));
  addEventListener('online', () => anunciarConexao('no_ar'));
} catch { /* fora do navegador (teste de módulo) */ }

/* ── A chave da intenção (0144) ────────────────────────────────────────
 *
 * Uma por INTENÇÃO, não por tentativa: as três tentativas de «lançar esta
 * leitura» carregam a mesma, e é isso que faz a segunda não gravar um
 * segundo registro. Quem sabe o que é uma intenção é a tela — o servidor
 * não tem como distinguir «tocou duas vezes» de «lançou duas leituras
 * iguais no mesmo minuto», e as duas coisas acontecem.
 */
export function chaveDeIntencao(): string {
  try {
    if (typeof crypto?.randomUUID === 'function') {
      return `t-${crypto.randomUUID()}`;
    }
  } catch { /* navegador antigo */ }
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

interface Opcoes {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  /** FormData vai cru: o navegador precisa montar o boundary do multipart. */
  formulario?: FormData;
  sinal?: AbortSignal;
  /** Resposta que não é JSON (HTML da pré-visualização, bytes do arquivo). */
  cru?: boolean;
  /**
   * A chave da intenção (`0144`). Passe a MESMA em toda retentativa.
   *
   * Quando ausente numa chamada que muda coisas, `api()` gera uma e a
   * reusa nas suas próprias tentativas — o que já cobre o caso do 4G que
   * engasga. Passar a sua é o que a fila off-line vai fazer, para a chave
   * sobreviver ao aplicativo fechado.
   */
  intencao?: string;
  /**
   * Quantas vezes reenviar quando a REDE falha (nunca quando o servidor
   * responde). Zero desliga.
   */
  tentativas?: number;
}

const MUDAM = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const espera = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function api<T = unknown>(caminho: string, o: Opcoes = {}): Promise<T> {
  const cabecalhos: Record<string, string> = {
    // A trilha de auditoria grava a origem do acesso (item 11 do briefing).
    // O dispositivo o servidor tira do user-agent; a origem quem informa
    // é o cliente, porque só ele sabe se é a tela ou um script.
    'x-origem': 'tela',
  };

  const t = token.ler();
  if (t) cabecalhos.authorization = `Bearer ${t}`;

  let corpo: BodyInit | undefined;
  if (o.formulario) {
    corpo = o.formulario;
  } else if (o.corpo !== undefined) {
    cabecalhos['content-type'] = 'application/json';
    corpo = JSON.stringify(o.corpo);
  }

  const metodo = o.metodo ?? (corpo ? 'POST' : 'GET');

  /*
   * A chave vai em toda chamada que MUDA coisas, sem a tela pedir.
   *
   * O caso que ela resolve não é o off-line: é o 4G que engasga, a
   * resposta que se perde e o técnico que toca de novo. Isso acontece com
   * conexão, hoje, e o servidor gravava as duas.
   */
  const intencao = o.intencao
    ?? (MUDAM.has(metodo) && !o.formulario ? chaveDeIntencao() : null);
  if (intencao) cabecalhos['idempotency-key'] = intencao;

  /*
   * Reenviar é seguro PORQUE a chave existe (`0144`). Sem ela, uma
   * retentativa automática seria uma duplicata automática — e é por isso
   * que este bloco não existia antes desta onda.
   */
  const limite = o.tentativas ?? (intencao ? 2 : 0);

  let r: Response | null = null;
  let ultimo: unknown = null;
  for (let tentativa = 0; tentativa <= limite; tentativa++) {
    try {
      r = await fetch(`/api${caminho}`, {
        method: metodo,
        headers: cabecalhos,
        body: corpo,
        signal: o.sinal,
      });
      anunciarConexao('no_ar');
      break;
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw e;
      ultimo = e;
      if (tentativa < limite) {
        anunciarConexao('tentando');
        await espera(600 * (tentativa + 1));
      }
    }
  }

  if (!r) {
    anunciarConexao('sem_rede');
    void ultimo;
    // Galpão com sinal ruim é o ambiente normal desta tela, não a exceção.
    throw new ErroApi(0, 'Sem conexão com o servidor. Verifique a rede e tente de novo.');
  }

  if (r.status === 401) {
    token.limpar();
    ouvintesSessao.forEach((f) => f());
    throw new ErroApi(401, await mensagemDe(r, 'Sua sessão expirou. Entre novamente.'));
  }

  if (!r.ok) {
    const corpoErro = await corpoJson(r);
    const d = corpoErro as { mensagem?: string; message?: string; campos?: CampoInvalido[]; recusas?: unknown } | null;
    throw new ErroApi(
      r.status,
      d?.mensagem ?? d?.message ?? `Falha na requisição (${r.status}).`,
      Array.isArray(d?.campos) ? d!.campos! : [],
      Array.isArray(d?.recusas) ? (d!.recusas as unknown[]) : undefined,
      d && typeof d === 'object' && !Array.isArray(d) ? (d as Record<string, unknown>) : undefined,
    );
  }

  if (o.cru) return (await r.text()) as unknown as T;
  if (r.status === 204) return undefined as T;
  /*
   * Corpo vazio com 200 é o `return null` de um controlador (onda 264):
   * o `r.json()` estourava «Unexpected end of JSON input» na cara de quem
   * só queria abrir a tela. Vazio vira `null`, que é o que o servidor quis
   * dizer.
   */
  const texto = await r.text();
  if (!texto.trim()) return null as T;
  return JSON.parse(texto) as T;
}

/**
 * O fluxo de eventos do servidor (SSE, onda 548) — também por aqui, para o
 * caminho `/api` continuar nascendo num lugar só.
 *
 * O `EventSource` não manda cabeçalho: a sessão não vai junto. Quem abre
 * passa na consulta o BILHETE do tempo real (60 s, só abre o fluxo, não é
 * sessão), tirado antes com `api('/tempo-real/bilhete', { metodo: 'POST' })`.
 */
/**
 * Desfaz na API a inscrição de push deste aparelho, com o token de QUEM SAI
 * (onda 548). Mora aqui porque o token entra em um lugar só (54.3); usa
 * `fetch` direto, e não o `api()`, porque a sessão já está saindo: um 401
 * aqui não pode derrubar a sessão de quem entrar em seguida, nem disparar
 * os ouvintes de sessão perdida. Nunca lança.
 */
export async function esquecerInscricaoNaApi(endpoint: string, tokenDeQuemSai: string): Promise<void> {
  await fetch('/api/avisos/push', {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json', 'x-origem': 'tela',
      authorization: `Bearer ${tokenDeQuemSai}`,
    },
    body: JSON.stringify({ endpoint }),
  }).catch(() => undefined);
}

export function abrirFluxo(caminho: string): EventSource {
  return new EventSource(`/api${caminho}`);
}

/**
 * Envio de arquivos com progresso.
 *
 * Usa XMLHttpRequest e não `fetch` por um motivo só: `fetch` não informa
 * progresso de UPLOAD. Doze fotos de celular num galpão com sinal ruim são
 * dezenas de megabytes; sem a barra, o técnico não sabe se está subindo ou
 * se travou, e toca de novo — o que dobra o envio.
 */
export function enviarFormulario<T = unknown>(
  caminho: string,
  formulario: FormData,
  aoProgredir?: (fracao: number) => void,
): Promise<T> {
  return new Promise<T>((resolver, recusar) => {
    const x = new XMLHttpRequest();
    x.open('POST', `/api${caminho}`);
    const t = token.ler();
    if (t) x.setRequestHeader('authorization', `Bearer ${t}`);
    x.setRequestHeader('x-origem', 'tela');
    // Nada de content-type aqui: quem monta o boundary do multipart é o
    // navegador, e defini-lo à mão quebra o parse no servidor.

    if (aoProgredir) {
      x.upload.onprogress = (e) => {
        if (e.lengthComputable) aoProgredir(e.loaded / e.total);
      };
    }

    x.onload = () => {
      let dados: unknown = null;
      try { dados = JSON.parse(x.responseText); } catch { /* corpo não-JSON */ }
      if (x.status >= 200 && x.status < 300) return resolver(dados as T);

      if (x.status === 401) {
        token.limpar();
        ouvintesSessao.forEach((f) => f());
      }
      const d = dados as { mensagem?: string; message?: string; campos?: CampoInvalido[]; recusas?: unknown } | null;
      recusar(new ErroApi(
        x.status,
        d?.mensagem ?? d?.message ?? `Falha no envio (${x.status}).`,
        Array.isArray(d?.campos) ? d!.campos! : [],
        Array.isArray(d?.recusas) ? (d!.recusas as unknown[]) : undefined,
      ));
    };
    x.onerror = () => recusar(new ErroApi(0, 'Sem conexão durante o envio das fotos.'));
    x.onabort = () => recusar(new ErroApi(0, 'Envio cancelado.'));
    x.send(formulario);
  });
}

/**
 * Os tipos que a bancada abre na própria aba — a MESMA lista da API
 * (`abreNaAba`, em `api/src/arquivos/arquivos.controller.ts`). Não há
 * código comum entre a tela e a API nesta casa; as duas se copiam, e o
 * 552.14 confere que são iguais.
 */
export const TIPOS_QUE_ABREM_NA_ABA = ['image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'application/pdf', 'video/mp4', 'text/plain'];
export const AUDIO_QUE_ABRE_NA_ABA = /^audio\/[a-z0-9.+-]+$/;

export function abreNaAba(mime: string | null | undefined): boolean {
  const base = String(mime ?? '').split(';')[0].trim().toLowerCase();
  return TIPOS_QUE_ABREM_NA_ABA.includes(base) || AUDIO_QUE_ABRE_NA_ABA.test(base);
}

/** A marca do HTML que a própria casa gera (`api/src/comum/documento-da-casa.ts`). */
export const DOCUMENTO_DA_CASA = 'x-documento-da-casa';

const ehHtml = (mime: string) => /^text\/html\s*(;|$)/i.test(mime);

/**
 * O documento pode ser mostrado numa aba: um tipo da lista, ou o HTML da
 * casa — que só sobrevive ao `baixarBlob` com a marca. O resto desce como
 * arquivo.
 */
export function abreNumaAba(mime: string): boolean {
  return abreNaAba(mime) || ehHtml(mime);
}

/**
 * Baixa um arquivo protegido e devolve os bytes.
 *
 * Serve tanto ao `<img>` do `FotoProtegida` quanto ao laudo do
 * `AcoesDoLaudo`: em ambos os casos o navegador sozinho não consegue buscar
 * o endereço, porque `/api` só responde com `Authorization`.
 *
 * O 401 aqui derruba a sessão como no `api()`. Sem isso, um token expirado
 * dava "não consegui abrir o arquivo" numa tela que continuava se dizendo
 * autenticada — e o técnico ficava tentando de novo o que nunca ia funcionar.
 */
export async function baixarBlob(caminho: string, sinal?: AbortSignal): Promise<Blob> {
  const t = token.ler();
  const r = await fetch(`/api${caminho}`, {
    headers: t ? { authorization: `Bearer ${t}`, 'x-origem': 'tela' } : { 'x-origem': 'tela' },
    signal: sinal,
  });
  if (r.status === 401) {
    token.limpar();
    ouvintesSessao.forEach((f) => f());
    throw new ErroApi(401, await mensagemDe(r, 'Sua sessão expirou. Entre novamente.'));
  }
  if (!r.ok) throw new ErroApi(r.status, await mensagemDe(r, 'Não consegui abrir o arquivo.'));
  /*
   * O Blob refeito com um tipo seguro (revisão da Tarefa 6 do Plano 2, onda
   * 550). Um endereço `blob:` tem a origem do sistema e ignora a CSP, o
   * `Content-Disposition` e o `nosniff` da resposta: um arquivo guardado
   * como `text/html` ou `image/svg+xml`, aberto numa aba, rodaria o script
   * dele com a sessão (o token mora no `localStorage`). Fica o tipo da
   * lista; o HTML só com a marca da casa; o resto vira
   * `application/octet-stream`, que nenhuma aba desenha.
   */
  const tipo = r.headers.get('content-type') ?? '';
  const daCasa = r.headers.get(DOCUMENTO_DA_CASA) === '1' && ehHtml(tipo);
  return new Blob([await r.blob()], {
    type: abreNaAba(tipo) || daCasa ? tipo : 'application/octet-stream',
  });
}

async function corpoJson(r: Response): Promise<unknown> {
  try { return await r.json(); } catch { return null; }
}

async function mensagemDe(r: Response, padrao: string): Promise<string> {
  const d = (await corpoJson(r)) as { mensagem?: string; message?: string } | null;
  return d?.mensagem ?? d?.message ?? padrao;
}

/** Mensagem legível de qualquer erro — inclusive os que não vêm da API. */
export function mensagemDoErro(e: unknown): string {
  if (e instanceof ErroApi) return e.message;
  if (e instanceof Error) return e.message;
  return 'Erro inesperado.';
}
